import { useEffect, useState } from "react";
import {
  ArrowRight,
  Check,
  ClipboardCheck,
  Database,
  Inbox,
  PlayCircle,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import SlideGuide, {
  type GuideSlide,
} from "@/components/training/SlideGuide";
import {
  LEGITSCRIPT_ONBOARDING_CHECKLIST,
  LEGITSCRIPT_ONBOARDING_SLIDES,
  LEGITSCRIPT_ONBOARDING_STEPS,
} from "@/data/legitScriptAnalystOnboarding";
import { trackEvent } from "@/utils/posthog";
import "./LegitScriptLonghandOnboarding.css";

const HANDOFF_CHECKLIST_STORAGE_KEY = "legitscript-analyst-onboarding-handoff-checklist-v1";
const HANDOFF_CHECKLIST_STORAGE_KEYS = [HANDOFF_CHECKLIST_STORAGE_KEY] as const;
const HANDOFF_CHECKLIST_IDS = new Set<string>(LEGITSCRIPT_ONBOARDING_CHECKLIST.map((item) => item.id));

const LOOP = [
  {
    label: "Classification queues",
    body: "Shared work that is ready to start. Use only the queue your lead approves.",
    icon: Inbox,
    accent: "#00c0f3",
  },
  {
    label: "My Work",
    body: "The runs you started, grouped by Running, Failed, Needs Review, and Done.",
    icon: PlayCircle,
    accent: "#9adbf9",
  },
  {
    label: "Review console",
    body: "Where you check each field against its evidence and record corrections.",
    icon: ClipboardCheck,
    accent: "#ccecfc",
  },
  {
    label: "LegitScript database",
    body: "The destination for a reviewed result when submission is allowed.",
    icon: Database,
    accent: "#00c0f3",
  },
] as const;

const WORKSPACE_AREAS = [
  {
    label: "Classification queues",
    body: "Shared work that is waiting to be started. Your lead tells you which queue to use.",
  },
  {
    label: "My Work",
    body: "The runs you have started and the current state of each one.",
  },
  {
    label: "Review",
    body: "The evidence and field-level controls you use when a run needs your decision.",
  },
] as const;

const WorkspaceOverviewSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-sky-300">Longhand at a glance</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
      This is the analyst workspace.
    </h1>
    <p className="mt-4 max-w-4xl text-lg leading-relaxed text-slate-300">
      Longhand is the desktop app where you start approved classification work, follow each run, and review results before they continue.
    </p>

    <div className="mt-7 grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
      <figure className="overflow-hidden rounded-2xl border border-white/15 bg-white shadow-2xl shadow-black/25">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-100 px-4 py-2.5">
          <div className="flex gap-2" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-600">Agents</span>
        </div>
        <img
          src="/legitscript-longhand-onboarding/analyst-work-board.png"
          alt="Longhand analyst workspace showing My Work above the Classification queues"
          loading="lazy"
          decoding="async"
          className="h-auto w-full"
        />
        <figcaption className="border-t border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-600">
          The Agents view puts My Work above the Classification queues. Live identifiers and counts are hidden.
        </figcaption>
      </figure>

      <div className="space-y-3">
        {WORKSPACE_AREAS.map((area, index) => (
          <div key={area.label} className="rounded-2xl border border-white/15 bg-white/[0.06] p-4">
            <p className="text-xs font-extrabold tracking-[0.15em] text-sky-300">{String(index + 1).padStart(2, "0")}</p>
            <h2 className="mt-2 text-lg font-bold text-white">{area.label}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{area.body}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const OperatingLoopSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-sky-300">How analyst work moves</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
      Work starts in a queue and stops when you need to review it.
    </h1>
    <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-300">
      Longhand tracks the run. You check the evidence and decide whether the result can continue.
    </p>

    <div className="mt-8 grid gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] lg:items-stretch">
      {LOOP.map((item, index) => {
        const Icon = item.icon;
        return (
          <div key={item.label} className="contents">
            <div className="rounded-2xl border border-white/15 bg-white/[0.06] p-5">
              <span
                className="flex h-12 w-12 items-center justify-center rounded-2xl"
                style={{ color: item.accent, backgroundColor: `${item.accent}1f`, border: `1px solid ${item.accent}66` }}
              >
                <Icon className="h-6 w-6" />
              </span>
              <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.14em] text-slate-500">Stage {index + 1}</p>
              <h2 className="mt-2 text-xl font-bold text-white">{item.label}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">{item.body}</p>
            </div>
            {index < LOOP.length - 1 && (
              <div className="flex items-center justify-center py-1 text-slate-500 lg:py-0" aria-hidden="true">
                <ArrowRight className="h-5 w-5 rotate-90 lg:rotate-0" />
              </div>
            )}
          </div>
        );
      })}
    </div>

    <div className="mt-7 flex items-start gap-3 rounded-2xl border border-cyan-300/25 bg-cyan-300/10 p-5 text-sm leading-relaxed text-cyan-50">
      <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
      <p><strong>The onboarding stop rule:</strong> do not start a batch until your lead names the practice queue, and do not submit a record whose evidence does not support your decision.</p>
    </div>
  </div>
);

const HandoffChecklistSlide = ({ printMode }: { printMode: boolean }) => {
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());
  const [progressLoaded, setProgressLoaded] = useState(false);
  const completedCount = completedItems.size;
  const totalItems = LEGITSCRIPT_ONBOARDING_CHECKLIST.length;
  const allComplete = completedCount === totalItems;

  useEffect(() => {
    try {
      const saved = localStorage.getItem(HANDOFF_CHECKLIST_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const savedIds = parsed.filter(
            (id): id is string => typeof id === "string" && HANDOFF_CHECKLIST_IDS.has(id),
          );
          setCompletedItems(new Set(savedIds));
        }
      }
    } catch {
      // The checklist still works for the current visit when storage is blocked.
    }
    setProgressLoaded(true);
  }, []);

  useEffect(() => {
    if (!progressLoaded) return;
    try {
      localStorage.setItem(HANDOFF_CHECKLIST_STORAGE_KEY, JSON.stringify([...completedItems]));
    } catch {
      // Keep the checklist in memory when storage is unavailable.
    }
  }, [completedItems, progressLoaded]);

  const toggleItem = (itemId: string) => {
    const next = new Set(completedItems);
    const completed = !next.has(itemId);
    if (completed) next.add(itemId);
    else next.delete(itemId);
    setCompletedItems(next);
    trackEvent("LegitScript Analyst Onboarding Checklist Updated", {
      item: itemId,
      completed,
      completed_items: next.size,
      total_items: totalItems,
    });
    if (next.size === totalItems) {
      trackEvent("LegitScript Analyst Onboarding Checklist Completed", {
        total_items: totalItems,
      });
    }
  };

  const clearChecklist = () => {
    setCompletedItems(new Set());
    try {
      localStorage.removeItem(HANDOFF_CHECKLIST_STORAGE_KEY);
    } catch {
      // The in-memory reset still succeeds.
    }
    trackEvent("LegitScript Analyst Onboarding Checklist Reset");
  };

  return (
    <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-5 sm:py-6">
      <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-sky-300">Step 10 of 10 · Final check</p>
      <h1 className="mt-3 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl">
        Complete your onboarding check.
      </h1>
      <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">
        Check each item as you finish it. Your progress is saved in this browser.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <p className="text-sm font-bold text-white">{completedCount} of {totalItems} complete</p>
        <div
          className="h-2 min-w-40 flex-1 overflow-hidden rounded-full bg-white/10"
          role="progressbar"
          aria-label="Onboarding checklist progress"
          aria-valuemin={0}
          aria-valuemax={totalItems}
          aria-valuenow={completedCount}
        >
          <div
            className="h-full rounded-full bg-cyan-300 transition-[width] duration-300"
            style={{ width: `${Math.round((completedCount / totalItems) * 100)}%` }}
          />
        </div>
        {!printMode && completedCount > 0 && (
          <button
            type="button"
            onClick={clearChecklist}
            className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-bold text-slate-200 hover:border-cyan-300 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
          >
            <RotateCcw className="h-4 w-4" /> Clear checklist
          </button>
        )}
      </div>

      <div className="mt-2 grid gap-x-8 md:grid-cols-2">
        {LEGITSCRIPT_ONBOARDING_CHECKLIST.map((item) => {
          const completed = completedItems.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => toggleItem(item.id)}
              disabled={printMode}
              aria-pressed={completed}
              className="group flex min-h-14 w-full items-start gap-4 border-b border-white/15 py-3 text-left text-white transition-colors hover:border-cyan-300/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300 disabled:cursor-default"
            >
              <span className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${completed ? "border-emerald-300 bg-emerald-400 text-emerald-950" : "border-white/35 bg-white/[0.04] text-transparent group-hover:border-cyan-300"}`}>
                <Check className="h-4 w-4" />
              </span>
              <span className={`text-sm font-semibold leading-relaxed sm:text-base ${completed ? "text-slate-400 line-through" : "text-slate-100"}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className={`mt-4 flex items-start gap-3 border-l-4 px-4 py-3 text-sm leading-relaxed ${allComplete ? "border-emerald-400 bg-emerald-400/10 text-emerald-50" : "border-cyan-300 bg-cyan-300/10 text-cyan-50"}`}>
        <ShieldCheck className={`mt-0.5 h-5 w-5 flex-shrink-0 ${allComplete ? "text-emerald-300" : "text-cyan-300"}`} />
        <p>{allComplete ? "Checklist complete. Continue to the final verification with your onboarding lead." : "Finish the checklist with your onboarding lead before you begin independent queue work."}</p>
      </div>
    </div>
  );
};

const renderCustomSlide = (slide: GuideSlide, context: { printMode: boolean }) => {
  if (slide.id === "workspace-overview") return <WorkspaceOverviewSlide />;
  if (slide.id === "operating-loop") return <OperatingLoopSlide />;
  if (slide.id === "handoff-checklist") return <HandoffChecklistSlide printMode={context.printMode} />;
  return null;
};

const LegitScriptLonghandOnboarding = () => (
  <SlideGuide
    name="LegitScript Analyst Onboarding"
    trackingName="LegitScript Analyst Onboarding"
    canonicalPath="/legitscript-longhand-onboarding"
    storageKey="legitscript-analyst-onboarding-progress-v1"
    slideStorageKey="legitscript-analyst-onboarding-slide-v1"
    additionalStorageKeys={HANDOFF_CHECKLIST_STORAGE_KEYS}
    branding={{
      rootClassName: "legitscript-guide",
      chrome: "light",
      logo: {
        src: "/legitscript-logo.svg",
        alt: "LegitScript",
        href: "https://www.legitscript.com/",
        ariaLabel: "Visit LegitScript.com",
        imageClassName: "h-8 w-auto sm:h-9",
      },
      paperBackdrop: "radial-gradient(circle at 8% 16%, rgba(0,192,243,.12), transparent 24%), radial-gradient(circle at 88% 82%, rgba(17,73,137,.08), transparent 25%), url('/legitscript-hex-pattern.png')",
      darkBackdrop: "radial-gradient(circle at 82% 18%, rgba(0,192,243,.18), transparent 28%), radial-gradient(circle at 10% 90%, rgba(154,219,249,.10), transparent 30%), url('/legitscript-hex-pattern.png')",
    }}
    seo={{
      title: "LegitScript Analyst Onboarding | Longhand",
      description: "A practical Longhand onboarding for LegitScript analysts. Join the workspace, connect the tools you need, and complete one supervised classification review.",
      keywords: [
        "LegitScript analyst onboarding",
        "Longhand onboarding",
        "classification review training",
        "analyst workflow onboarding",
      ],
      image: "https://www.technical-leaders.com/legitscript-longhand-onboarding-og.png",
    }}
    schema={{
      description: "Install Longhand, join the LegitScript organization, connect Google Workspace and the Browser Agent, verify the Analyst workspace, start one supervised batch, review one classification, and hand off the evidence.",
      totalTime: "PT59M",
      supplies: [
        "A Longhand invitation sent to your LegitScript work email",
        "A macOS or Windows computer",
        "Google Chrome",
        "The approved Google Workspace account for LegitScript work",
        "A designated practice queue",
        "A LegitScript onboarding lead for the first run",
      ],
    }}
    cover={{
      kicker: "LegitScript analyst training",
      title: "LegitScript Analyst",
      accent: "Onboarding",
      description: "Learn the workspace, connect the tools you need, and complete one classification with your onboarding lead.",
      badges: ["55–60 minutes", "macOS + Windows"],
      finishLineTitle: "One supervised classification",
      finishLineBody: "You can find the right queue, follow the run in My Work, review the evidence, and show your lead the final result or exact stopping point.",
      logo: {
        src: "/longhand-logo.png",
        alt: "Longhand",
        imageClassName: "h-8 w-auto sm:h-9",
      },
    }}
    outcomes={{
      eyebrow: "What you'll be able to do",
      heading: "By the end, you should know where to work and when to stop.",
      items: [
        "Sign in to the LegitScript workspace",
        "Connect Google Workspace and the Browser Agent",
        "Find My Work and the Classification queues",
        "Start only the queue your lead approves",
        "Review each field against its evidence",
        "Record the outcome or the exact point where work stopped",
      ],
      summary: "This is a guided first run, not a full tour of Longhand. The goal is to complete the basic setup and know what to do when a run needs your attention.",
    }}
    roadmapHeading="The setup and one supervised classification, from start to finish."
    steps={LEGITSCRIPT_ONBOARDING_STEPS}
    slides={LEGITSCRIPT_ONBOARDING_SLIDES}
    finish={{
      eyebrow: "Onboarding complete",
      heading: "You are ready for supervised work.",
      body: "Use the same routine each time: confirm the queue, let the run proceed, review every field against its evidence, and capture the exact state when something stops.",
      cards: [],
    }}
    contentsExtras={[
      { slideId: "workspace-overview", label: "The analyst workspace" },
      { slideId: "operating-loop", label: "How analyst work moves" },
    ]}
    renderCustomSlide={renderCustomSlide}
  />
);

export default LegitScriptLonghandOnboarding;
